import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["customSelect", "loader"]
  static values = {
    pageLanguage: { type: String, default: "en" },
    pollInterval: { type: Number, default: 1500 },
    pollTimeout: { type: Number, default: 7000 },
    stableMs: { type: Number, default: 350 }
  }

  connect() {
    this.shouldOpenSelect2AfterLoad = false

    this.handleBeforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)

    this.bindCustomChange()
    this.setupGoogleTranslate()
    this.startPollingGoogleCombo()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.clearPollTimer()
  }

  beforeCache() {
    this.clearPollTimer()
  }

  openLanguageDropdown(event) {
    if (event) event.preventDefault()

    this.shouldOpenSelect2AfterLoad = true

    if (this.hasCustomSelectTarget && this.customSelectTarget.options.length > 1) {
      this.hideBootstrapDropdown()
      this.openSelect2()
    }
  }

  openSelect2() {
    if (!window.jQuery || !this.hasCustomSelectTarget) return

    this.showSelect2Container()

    const $select = jQuery(this.customSelectTarget)

    if (!$select.data("select2")) {
      setTimeout(() => this.openSelect2(), 150)
      return
    }

    setTimeout(() => {
      $select.select2("open")
    }, 100)
  }

  showSelect2Container() {
    const wrapper = this.customSelectTarget.closest(".goog-te-gadget")
    if (!wrapper) return

    wrapper.classList.remove("d-none")
    this.loaderTarget.classList.add("d-none")
  }

  hideBootstrapDropdown() {
    const btn = document.getElementById("langDropdownBtn")
    if (!btn || !window.bootstrap) return

    const dropdown =
      bootstrap.Dropdown.getInstance(btn) ||
      bootstrap.Dropdown.getOrCreateInstance(btn)

    dropdown.hide()
  }

  bindCustomChange() {
    if (!this.hasCustomSelectTarget) return
    if (this.customSelectTarget.dataset.changeBound === "1") return

    this.customSelectTarget.dataset.changeBound = "1"

    this.customSelectTarget.addEventListener("change", async () => {
      if (window.__gtSuppressLangChange) return
      if (this.customSelectTarget.dataset.switching === "1") return

      this.customSelectTarget.dataset.switching = "1"

      try {
        const lang = this.customSelectTarget.value
        localStorage.setItem("selected_lang", lang)

        const gtCombo = document.querySelector(".goog-te-combo")
        if (!gtCombo) return

        this.showFullLoader()
        await this.nextFrame()

        if (lang === "en") {
          await this.resetTranslation(gtCombo)
        } else {
          gtCombo.value = lang
          gtCombo.dispatchEvent(new Event("change"))
          await this.waitForTranslationDone()
        }
      } finally {
        this.hideFullLoader()
        this.customSelectTarget.dataset.switching = "0"
      }
    })
  }

  setupGoogleTranslate() {
    window.__gtSuppressLangChange = false

    window.googleTranslateElementInit = () => {
      setTimeout(() => {
        const isInitialized = document.querySelector(".goog-te-combo")
        const translateContainer = document.getElementById("google_translate_element")

        if (!isInitialized && translateContainer && window.google?.translate) {
          new google.translate.TranslateElement(
            {
              pageLanguage: this.pageLanguageValue,
              autoDisplay: false
            },
            "google_translate_element"
          )
        }
      }, 1000)
    }

    if (!window.google || !window.google.translate) {
      this.loadGoogleScript()
    } else {
      window.googleTranslateElementInit()
    }
  }

  loadGoogleScript() {
    const existingScript = document.querySelector('script[data-google-translate="true"]')

    if (existingScript && !window.google?.translate) {
      console.warn("[GT] old script exists but Google not ready, removing")
      existingScript.remove()
    } else if (existingScript) {
      return
    }

    const script = document.createElement("script")
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
    script.async = true
    script.dataset.googleTranslate = "true"

    script.onload = () => {
      console.log("[GT] script loaded")
    }

    script.onerror = () => {
      console.error("[GT] script failed, retrying...")
      script.remove()

      setTimeout(() => {
        this.loadGoogleScript()
      }, 3000)
    }

    document.head.appendChild(script)

    setTimeout(() => {
      if (!window.google?.translate) {
        console.warn("[GT] script stuck, retrying...")
        script.remove()
        this.loadGoogleScript()
      }
    }, 10000)
  }

  startPollingGoogleCombo() {
    if (!this.hasCustomSelectTarget) return

    this.clearPollTimer()

    this.pollTimer = setInterval(() => {
      const gtCombo = document.querySelector(".goog-te-combo")
      if (!gtCombo || gtCombo.options.length < 2) return

      this.populateCustomDropdown(gtCombo)
      gtCombo.style.display = "none"

      this.clearPollTimer()
      this.restoreSelectedLanguage()
      this.refreshSelect2()

      if (this.shouldOpenSelect2AfterLoad) {
        this.shouldOpenSelect2AfterLoad = false
        this.hideBootstrapDropdown()

        setTimeout(() => {
          this.openSelect2()
        }, 250)
      }
    }, this.pollIntervalValue)
  }

  populateCustomDropdown(gtCombo) {
    const currentValue = this.customSelectTarget.value
    this.customSelectTarget.innerHTML = ""

    const options = []
    // Add English manually
    const englishOpt = document.createElement("option")
    englishOpt.value = "en"
    englishOpt.textContent = "English"
    options.push(englishOpt)

    for (let i = 0; i < gtCombo.options.length; i++) {
      const option = gtCombo.options[i]
      if (!option.value) continue
      if (option.value === "en") continue

      const opt = document.createElement("option")
      opt.value = option.value
      opt.textContent = option.textContent
      options.push(opt)
    }

    // English included in normal sort order
    options.sort((a, b) => a.textContent.localeCompare(b.textContent))

    options.forEach((opt) => this.customSelectTarget.appendChild(opt))

    if (currentValue && this.customSelectTarget.querySelector(`option[value="${currentValue}"]`)) {
      this.customSelectTarget.value = currentValue
    } else {
      this.customSelectTarget.value = "en"
    }
    this.refreshSelect2()
  }
  
  refreshSelect2() {
    // if (!window.jQuery || !this.hasCustomSelectTarget) return

    const $select = jQuery(this.customSelectTarget)

    // if ($select.data("select2")) {
      $select.trigger("change.select2")
    // }
  }

  select2Open() {
    setTimeout(() => {
      const input = document.querySelector(".select2-container--open .select2-search__field")
      if (input) input.focus()
    }, 50)
  }

  restoreSelectedLanguage() {
    const last = localStorage.getItem("selected_lang")
    if (!last) return

    if (!this.customSelectTarget.querySelector(`option[value="${last}"]`)) return

    window.__gtSuppressLangChange = true
    this.customSelectTarget.value = last
    this.refreshSelect2()

    setTimeout(() => {
      window.__gtSuppressLangChange = false
    }, 0)
  }

  async resetTranslation(gtCombo) {
    const host = window.location.hostname
    this.deleteCookie("googtrans")
    this.deleteCookie("googtrans", "." + host)

    gtCombo.value = ""
    gtCombo.dispatchEvent(new Event("change"))

    await this.waitForTranslationDone()
    this.tryClickShowOriginal()
  }

  tryClickShowOriginal() {
    const bannerFrame = document.querySelector("iframe.skiptranslate")
    if (!bannerFrame) return

    setTimeout(() => {
      try {
        const doc = bannerFrame.contentDocument || bannerFrame.contentWindow?.document
        if (!doc) return

        const btn = Array.from(doc.getElementsByTagName("button")).find((button) =>
          /show original/i.test(button.innerText)
        )

        if (btn) btn.click()
      } catch (e) {
        console.warn("Could not access Google Translate iframe:", e)
      }
    }, 50)
  }

  waitForTranslationDone({ timeoutMs = this.pollTimeoutValue, stableMs = this.stableMsValue } = {}) {
    return new Promise((resolve) => {
      let lastChange = Date.now()
      const start = Date.now()

      const observer = new MutationObserver(() => {
        lastChange = Date.now()
      })

      observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true
      })

      const tick = setInterval(() => {
        const now = Date.now()

        if (now - lastChange > stableMs) {
          cleanup()
          resolve(true)
          return
        }

        if (now - start > timeoutMs) {
          cleanup()
          resolve(false)
        }
      }, 1000)

      const cleanup = () => {
        clearInterval(tick)
        observer.disconnect()
      }
    })
  }

  deleteCookie(name, domain = null) {
    document.cookie =
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;` +
      (domain ? ` domain=${domain};` : "")
  }

  showFullLoader() {
    const el = document.getElementById("page-overlay")
    if (!el) return

    el.style.display = "block"
    const inner = el.querySelector("div")
    if (inner) {
      inner.innerHTML = `
        <span class="spinner-border spinner-border-sm text-primary me-2" aria-hidden="true"></span>
        <span class="text-primary">Translating…</span>
      `
    }
  }

  hideFullLoader() {
    const el = document.getElementById("page-overlay")
    if (el) el.style.display = "none"
  }

  nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }
}